import { useEffect, useState } from "react";
import moment from "moment";
import styles from "./cashouts.module.scss";
import {
  BUTTON,
  DATE_FILTER_FORMAT,
  FINISHED_CASHOUTS_TAB,
  HEADER,
  PENDING_CASHOUTS_TAB,
  TABLE_HEADERS,
} from "./constants";
import DatePicker from "../../components/common/DatePicker";
import SearchInput from "../../components/common/searchInput";
import { Tab, Tabs } from "@mui/material";
import CashoutsTableShimmer from "./CashoutsTableShimmer";
import {
  getFinishedCashouts,
  getPendingCashouts,
} from "../../setup/api/services/cashouts";
import CashoutsListItem from "./CashoutsListItem";
import Paginations from "../../components/common/Pagination";
import { AsyncPaginate } from "react-select-async-paginate";
import { getAllOrganizations } from "../../setup/api/services/organization";
import { IOrganizations } from "../../setup/types/Organisation";
import { SingleValue } from "react-select";
import { useTranslation } from "react-i18next";
import { useSettlementActionsConfig } from "../../hooks/useShouldHideSettlementActions";

const PAGE_SIZE = 10;

const Cashouts = () => {
  const { t } = useTranslation();
  const [cashouts, setCashouts] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [transactionSearchValue, setTransactionSearchValue] = useState("");
  const [tabType, setTabType] = useState("pending");
  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [organization, setOrganization] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [settledAtFrom, setSettledAtFrom] = useState<Date | null>(null);
  const [settledAtTo, setSettledAtTo] = useState<Date | null>(null);
  const { config: settlementActionsConfig } = useSettlementActionsConfig();

  // The picker yields a plain day, so the range covers it whole: 00:00 → 23:59.
  const settledAtRange = {
    settledAtFrom: settledAtFrom
      ? moment(settledAtFrom).startOf("day").format(DATE_FILTER_FORMAT)
      : "",
    settledAtTo: settledAtTo
      ? moment(settledAtTo).endOf("day").format(DATE_FILTER_FORMAT)
      : "",
  };

  const fetchPendingCashouts = async () => {
    setLoading(true);
    try {
      const body = {
        referenceTransactionId: transactionSearchValue,
        contractId: searchValue,
        ...settledAtRange,
        page: page - 1,
        size: PAGE_SIZE,
        organizationCode: organization?.value,
      };
      const response = await getPendingCashouts(body);
      setCashouts(response.data.content);
      setPagesCount(response.data.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setCashouts([]);
    }
  };

  const fetchFinishedCashouts = async () => {
    setLoading(true);
    try {
      const body = {
        referenceTransactionId: transactionSearchValue,
        contractId: searchValue,
        ...settledAtRange,
        page: page - 1,
        size: PAGE_SIZE,
        organizationCode: organization?.value,
      };
      const response = await getFinishedCashouts(body);
      setCashouts(response.data.content);
      setPagesCount(response.data.totalPages);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setCashouts([]);
    }
  };

  useEffect(() => {
    if (tabType === "pending") {
      fetchPendingCashouts();
    } else {
      fetchFinishedCashouts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tabType,
    searchValue,
    transactionSearchValue,
    page,
    organization,
    settledAtFrom,
    settledAtTo,
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabType(newValue);
    setPage(1);
  };

  const handlePaginationChange = (value: number) => {
    setPage(value);
  };

  const handleOrganizationSelected = (
    newValue: SingleValue<{
      value: string;
      label: string;
    }>,
    _
  ) => {
    setOrganization(newValue);
    setPage(1);
  };

  const loadOptions = async (q: string, _, additional) => {
    const waffyOption = {
      value: "WAFFY",
      label: "WAFFY",
    };
    const response = await getAllOrganizations(additional.page, PAGE_SIZE, q);
    const options = response.data.content.map((org: IOrganizations) => ({
      value: org.code,
      label: org.name,
    }));

    if (additional.page === 0) {
      options.unshift(waffyOption);
    }

    return {
      options: options,
      hasMore: response.data.totalPages > additional.page + 1,
      additional: {
        page: additional.page + 1,
      },
    };
  };

  return (
    <div className={styles["page_layout"]}>
      <h1>{HEADER}</h1>
      <div className={styles.info_banner}>
        {t("lbl_auto_cashout_default_address_warning")}
      </div>
      <div className={styles.header_container}>
        <div className={styles["right_section"]}>
          <Tabs
            style={{ alignItems: "center" }}
            className={styles.tab_wrapper}
            value={tabType}
            onChange={handleTabChange}>
            <Tab
              value="pending"
              label={PENDING_CASHOUTS_TAB}
              sx={{
                paddingLeft: "1rem",
                paddingRight: "1rem",
                fontSize: { xs: "0.75em", md: "1em" },
              }}
            />
          </Tabs>
          <div style={{ marginLeft: "8px" }}>
            <AsyncPaginate
              additional={{ page: 0 }}
              value={organization}
              loadOptions={loadOptions}
              onChange={handleOrganizationSelected}
              placeholder={t("lbl_choose_organisation")}
              styles={{
                control: (base) => ({
                  ...base,
                  width: 200,
                  height: "3rem",
                  padding: "0 1rem",
                  border: "1px solid #E2E8EE",
                  borderRadius: "6rem",
                }),
              }}
            />
          </div>
          <div className={styles["filter_date_picker"]}>
            <DatePicker
              value={settledAtFrom}
              placeholder={BUTTON.SETTLED_AT_START_DATE}
              handleChange={(date) => {
                setSettledAtFrom(date);
                setPage(1);
              }}
              maxDate={settledAtTo ?? new Date()}
            />
          </div>
          <div className={styles["filter_date_picker"]}>
            <DatePicker
              value={settledAtTo}
              placeholder={BUTTON.SETTLED_AT_END_DATE}
              handleChange={(date) => {
                setSettledAtTo(date);
                setPage(1);
              }}
              minDate={settledAtFrom ?? undefined}
              maxDate={new Date()}
            />
          </div>
        </div>
        <div className={styles["left_section"]}>
          <SearchInput
            dir="ltr"
            placeholder={BUTTON.SEARCH}
            value={searchValue}
            style={{ height: "35px", width: "252px" }}
            handleChange={(event) => {
              setSearchValue(event.target.value);
            }}
          />
          <SearchInput
            dir="ltr"
            placeholder={BUTTON.SEARCH_TRANS}
            value={transactionSearchValue}
            style={{ height: "35px", width: "252px" }}
            handleChange={(event) => {
              setTransactionSearchValue(event.target.value);
            }}
          />
        </div>
      </div>

      <table className={styles.table}>
        <tr className={styles.table_header}>
          <th className={styles.hint}>{TABLE_HEADERS.TRANSACTION_NAME}</th>
          <th className={styles.hint}>{TABLE_HEADERS.TRANSACTION_ID}</th>
          <th className={styles.hint}>{TABLE_HEADERS.CASHOUTS_NUMBER}</th>
          <th className={styles.hint}>{TABLE_HEADERS.TOTAL_AMOUNT}</th>
          <th className={styles.hint}>{TABLE_HEADERS.STATUS}</th>
          <th className={styles.hint}>
            {TABLE_HEADERS.AUTO_CASHOUT_ELIGIBILITY}
          </th>
          <th className={styles.hint}>{TABLE_HEADERS.PARTIES_DATA}</th>
          <th className={styles.hint}>
            {TABLE_HEADERS.GRACE_PERIOD_REMAINING}
          </th>
        </tr>
        {loading ? (
          <CashoutsTableShimmer />
        ) : (
          <tbody className={styles.table_body}>
            {cashouts?.map((cashout, index) => (
              <CashoutsListItem
                key={index}
                contract={cashout}
                settlementActionsConfig={settlementActionsConfig}
              />
            ))}
          </tbody>
        )}
      </table>

      <>
        <div className={cashouts?.length ? "paginatorContainer" : "hide"}>
          <Paginations
            pagesCount={pagesCount}
            length={pagesCount}
            page={page}
            handlePaginationChange={handlePaginationChange}
          />
        </div>
      </>
    </div>
  );
};

export default Cashouts;
